import React, { Suspense, lazy, useCallback, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { checkPaymentMethodStatus, isRouteAllowed, shouldHardGate } from './utils/paymentMethodStatus';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FirmProvider } from './contexts/FirmContext';
import { useFirm } from './contexts/FirmContext.definition';
import { FirmClientIdentityIndexProvider } from './contexts/FirmClientIdentityIndexContext';
import { ToastContainer } from 'react-toastify';
import { useTheme } from '@/contexts/ThemeContext';
import ErrorReportingFAB from './components/ErrorReportingFAB';
import { ErrorReportingContext } from './contexts/ErrorReportingContext';
import { useDismissStaleToastsOnNavigate } from './hooks/useDismissStaleToastsOnNavigate';
import { HelmetProvider } from 'react-helmet-async';
import 'react-toastify/dist/ReactToastify.css';

// All Pages (lazy loaded for optimal bundle splitting)
// Public pages (landing, legal)
const LandingPage = lazy(() => import('./pages/landing/LandingPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/legal/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/legal/TermsOfServicePage'));
const VerifyFacilityPage = lazy(() => import('./pages/public/VerifyFacilityPage').then(m => ({ default: m.VerifyFacilityPage })));
const ClaimFacilityPage = lazy(() => import('./pages/public/ClaimFacilityPage'));
const ProviderUploadPage = lazy(() => import('./pages/public/ProviderUploadPage').then(m => ({ default: m.ProviderUploadPage })));

// Auth pages - loaded immediately as they're entry points
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const AcceptInvitationPage = lazy(() => import('./pages/AcceptInvitationPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const AuthActionPage = lazy(() => import('./pages/AuthActionPage'));

// Main app pages
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const ClientDetailPage = lazy(() => import('./pages/ClientDetailPage'));
const PracticesPage = lazy(() => import('./pages/PracticesPage'));
const PracticeDetailPage = lazy(() => import('./pages/PracticeDetailPage'));
const ActionItemsPage = lazy(() => import('./pages/ActionItemsPage'));
const RequestStatusPage = lazy(() => import('./pages/RequestStatusPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const FinancialActivityPage = lazy(() => import('./pages/FinancialActivityPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage'));
const TemplateEditorPage = lazy(() => import('./pages/TemplateEditorPage'));
const NewRequestPage = lazy(() => import('./pages/NewRequestPage'));
const EmailLogsPage = lazy(() => import('./pages/EmailLogsPage'));
const AllInvoicesPage = lazy(() => import('./pages/AllInvoicesPage'));
const FirmStatementPage = lazy(() => import('./pages/FirmStatementPage'));
const PaymentHistoryPage = lazy(() => import('./pages/PaymentHistoryPage'));

const BulkOperationsPage = lazy(() => import('./pages/BulkOperationsPage'));
const ViewAllDocumentsPage = lazy(() => import('./pages/ViewAllDocumentsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Admin Pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const OperationalHealthPage = lazy(() => import('./pages/admin/OperationalHealthPage'));
const FirmsManagement = lazy(() => import('./pages/admin/FirmsManagement'));
const UsersManagement = lazy(() => import('./pages/admin/UsersManagement'));
const BillingOverview = lazy(() => import('./pages/admin/BillingOverview'));
const StatementInvoicesManagement = lazy(() => import('./pages/admin/StatementInvoicesManagement'));
const AuditLogsViewer = lazy(() => import('./pages/admin/AuditLogsViewer'));
const DataBrowser = lazy(() => import('./pages/admin/DataBrowser'));
const SystemSettings = lazy(() => import('./pages/admin/SystemSettings'));
const DevTesting = lazy(() => import('./pages/admin/DevTesting'));
const PromoCodesManagement = lazy(() => import('./pages/admin/PromoCodesManagement'));
const ErrorReportsViewer = lazy(() => import('./pages/admin/ErrorReportsViewer'));
const EmailReviewQueuePage = lazy(() => import('./pages/admin/EmailReviewQueuePage'));
const GlobalPromotionCandidatesPage = lazy(() => import('./pages/admin/GlobalPromotionCandidatesPage'));
const FacilityClaimsPage = lazy(() => import('./pages/admin/FacilityClaimsPage'));
const BlockedEmailsPage = lazy(() => import('./pages/admin/BlockedEmailsPage'));
const ErrorReportingModal = lazy(() => import('./components/modals/ErrorReportingModal'));

// Loading spinner component
function LoadingSpinner() {
  return (
    <div className="min-h-dvh flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="spinner h-12 w-12 border-4 border-brand-orange mx-auto mb-4"></div>
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { currentUser, userProfile, initializing, isAdmin, isImpersonating } = useAuth();
  const location = useLocation();

  // Use centralized firm data from context (real-time updates)
  const { firm, loading: loadingFirm } = useFirm();

  // Wait for auth to initialize AND firm to load
  if (initializing || loadingFirm) {
    return <LoadingSpinner />;
  }

  if (!currentUser) {
    // Come back here after signing in (e.g. a client page opened in a new tab).
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }

  // Admin users should go to admin dashboard (unless impersonating a firm)
  if (isAdmin && !isImpersonating) {
    return <Navigate to="/admin" replace />;
  }

  // Check if email is verified (skip for dev/testing environments)
  const requireEmailVerification = import.meta.env.PROD || import.meta.env.VITE_REQUIRE_EMAIL_VERIFICATION === 'true';
  if (requireEmailVerification && !currentUser.emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  // Check if onboarding is completed - we use howDidYouHear as the indicator
  // since it's always collected at the end of the onboarding flow
  if (userProfile && !userProfile.howDidYouHear) {
    return <Navigate to="/onboarding" replace />;
  }

  // Check billing enforcement (skip for admins impersonating)
  // Hard-gate (redirect to /billing) only for past_due/unpaid — these firms had a working
  // subscription that broke and need urgent attention.
  // Soft-gate (free roaming, banner up top, client-creation blocked at the action layer)
  // applies to `missing` (new firms with no card yet) so they can explore the product.
  if (!isImpersonating && firm) {
    const paymentStatus = checkPaymentMethodStatus(firm);
    const currentPath = location.pathname;

    if (
      paymentStatus.status !== 'valid' &&
      shouldHardGate(paymentStatus.reason) &&
      !isRouteAllowed(currentPath)
    ) {
      return <Navigate to="/billing" replace />;
    }
  }

  return <>{children}</>;
}


// Auth Only Route (requires authentication but not custom claims)
interface AuthOnlyRouteProps {
  children: React.ReactNode;
}

function AuthOnlyRoute({ children }: AuthOnlyRouteProps) {
  const { currentUser, initializing } = useAuth();

  if (initializing) {
    return <LoadingSpinner />;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Admin Route Guard Component
interface AdminRouteProps {
  children: React.ReactNode;
}

function AdminRoute({ children }: AdminRouteProps) {
  const { currentUser, isAdmin, initializing } = useAuth();

  if (initializing) {
    return <LoadingSpinner />;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    // Non-admin users go to regular dashboard
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      {children}
    </Suspense>
  );
}

function AppRoutes() {
  const [isErrorReportingModalOpen, setIsErrorReportingModalOpen] = useState(false);
  const { currentUser } = useAuth();
  const { resolvedTheme } = useTheme();
  useDismissStaleToastsOnNavigate();
  const openErrorReport = useCallback(() => setIsErrorReportingModalOpen(true), []);

  return (
    <ErrorReportingContext.Provider value={currentUser ? openErrorReport : null}>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public landing page */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />

          {/* Public auth routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
          <Route path="/auth/action" element={<AuthActionPage />} />
          <Route path="/upload/:token" element={<ProviderUploadPage />} />
          <Route path="/verify-facility/:token" element={<VerifyFacilityPage />} />
          <Route path="/claim-facility" element={<ClaimFacilityPage />} />

          {/* Auth-only routes (requires auth but not custom claims) */}
          <Route
            path="/verify-email"
            element={
              <AuthOnlyRoute>
                <VerifyEmailPage />
              </AuthOnlyRoute>
            }
          />
          <Route
            path="/onboarding"
            element={
              <AuthOnlyRoute>
                <OnboardingPage />
              </AuthOnlyRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/firms"
            element={
              <AdminRoute>
                <FirmsManagement />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <UsersManagement />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/billing"
            element={
              <AdminRoute>
                <BillingOverview />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/billing/statements"
            element={
              <AdminRoute>
                <StatementInvoicesManagement />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/audit-logs"
            element={
              <AdminRoute>
                <AuditLogsViewer />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/data"
            element={
              <AdminRoute>
                <DataBrowser />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <AdminRoute>
                <SystemSettings />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/dev"
            element={
              <AdminRoute>
                <DevTesting />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/promo-codes"
            element={
              <AdminRoute>
                <PromoCodesManagement />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/error-reports"
            element={
              <AdminRoute>
                <ErrorReportsViewer />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/operational-health"
            element={
              <AdminRoute>
                <OperationalHealthPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/email-review"
            element={
              <AdminRoute>
                <EmailReviewQueuePage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/facility-promotions"
            element={
              <AdminRoute>
                <GlobalPromotionCandidatesPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/facility-claims"
            element={
              <AdminRoute>
                <FacilityClaimsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/blocked-emails"
            element={
              <AdminRoute>
                <BlockedEmailsPage />
              </AdminRoute>
            }
          />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <ProtectedRoute>
                <ClientsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/:clientId"
            element={
              <ProtectedRoute>
                <ClientDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/:clientId/documents"
            element={
              <ProtectedRoute>
                <ViewAllDocumentsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/practices"
            element={
              <ProtectedRoute>
                <PracticesPage />
              </ProtectedRoute>
            }
          />
          <Route path="/providers" element={<Navigate to="/practices" replace />} />
          <Route
            path="/practices/:firmFacilityId"
            element={
              <ProtectedRoute>
                <PracticeDetailPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/action-items"
            element={
              <ProtectedRoute>
                <ActionItemsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/request-status"
            element={
              <ProtectedRoute>
                <RequestStatusPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/requests/new"
            element={
              <ProtectedRoute>
                <NewRequestPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/templates"
            element={
              <ProtectedRoute>
                <TemplatesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates/new"
            element={
              <ProtectedRoute>
                <TemplateEditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates/:templateId/edit"
            element={
              <ProtectedRoute>
                <TemplateEditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email-logs"
            element={
              <ProtectedRoute>
                <EmailLogsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/bulk-operations"
            element={
              <ProtectedRoute>
                <BulkOperationsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <BillingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/activity"
            element={
              <ProtectedRoute>
                <FinancialActivityPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/invoices"
            element={
              <ProtectedRoute>
                <AllInvoicesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/firm-statement"
            element={
              <ProtectedRoute>
                <FirmStatementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/payments"
            element={
              <ProtectedRoute>
                <PaymentHistoryPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>

      {/* Global Error Reporting UI. The FAB is the fallback entry; app chrome
          docks the same actions (see ErrorReportingContext). */}
      {currentUser && (
        <>
          <ErrorReportingFAB onClick={openErrorReport} />
          {isErrorReportingModalOpen && (
            <Suspense fallback={null}>
              <ErrorReportingModal onClose={() => setIsErrorReportingModalOpen(false)} />
            </Suspense>
          )}
        </>
      )}

      {/* Toast notifications */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={resolvedTheme}
      />
    </ErrorReportingContext.Provider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <FirmProvider>
          <FirmClientIdentityIndexProvider>
            <Router>
              <AppRoutes />
            </Router>
          </FirmClientIdentityIndexProvider>
        </FirmProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
